import { RetryButton } from '@/shared/components/RetryButton';
import { fileReadMessage } from '../fileReadMessage';
import { useFileObjectUrl } from '../hooks/useFileObjectUrl';
import { HTML_PREVIEW_CSP, withInjectedCsp } from './htmlPreviewCsp';

/*
  `csp` is a real iframe attribute and is not in React's `iframe` prop types.
  A local module augmentation types it here rather than letting an `any` or an
  unchecked spread cross a module seam. If this ever stops type-checking or
  leaks awkwardly across the api/app/tests `tsconfig` projects, the sanctioned
  fallback is one commented single-attribute spread on the element below —
  `{...{ csp: HTML_PREVIEW_CSP }}` — and nothing wider.
*/
declare module 'react' {
  // The type parameter is React's own and must be redeclared to merge with the
  // interface, but nothing here uses it — hence the disable, scoped to the line.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface IframeHTMLAttributes<T> {
    csp?: string;
  }
}

/**
 * Hand the fetched document to `withInjectedCsp` before the blob is built.
 *
 * Module-level, deliberately: `useFileObjectUrl` treats `transform` as an
 * effect dependency, so an inline arrow would be a fresh function on every
 * render and would revoke the object URL and re-fetch the file every render.
 */
const injectPreviewCsp = (html: string) =>
  withInjectedCsp(html, HTML_PREVIEW_CSP);

/**
 * An agent-authored HTML page, rendered as a laid-out document with its own CSS
 * and its own scripts running, inside a sandboxed frame.
 *
 * Read as a blob for the reason `ImagePreview` and `PdfPreview` give: the
 * content endpoint needs a bearer token a frame cannot send, and the blob is
 * typed `text/html` whatever the server stored, because a document labelled
 * `application/octet-stream` would download instead of rendering.
 *
 * The markup is **not** sanitized — no `<script>` stripping, no DOMPurify.
 * Scripts are meant to run; the platform's sandbox contains them more
 * completely than a filter would, and without a dependency. This is the
 * opposite trade from `Markdown.tsx`'s `skipHtml`, which renders agent prose
 * inside the app's own DOM, where there is no sandbox at all.
 *
 * `srcdoc` is not used: it makes the document's base URL the parent's, so a
 * relative reference would issue a request against the app's own origin, and
 * the whole document would have to travel through a React prop as an
 * attribute. `src` with a blob URL is the chosen form.
 *
 * ## Why all three mechanisms are present
 *
 * 1. `sandbox="allow-scripts"` — an opaque origin. A `blob:` URL otherwise
 *    inherits the creating document's origin, so without the sandbox the page
 *    would be *same-origin with this app* and could read its storage and its
 *    session. The sandbox is what makes the origin opaque despite the blob.
 * 2. The injected `<meta http-equiv="Content-Security-Policy">` — enforcement
 *    that travels inside the document, because the `csp` attribute's browser
 *    support is incomplete and no requirement may depend on it alone.
 * 3. `csp={HTML_PREVIEW_CSP}` — where it is supported, the policy is applied by
 *    the embedder, before the document's own bytes are parsed at all.
 *
 * They overlap on purpose: each covers a gap the others leave *in the framed
 * case*, and the policy text comes from one exported constant so the attribute
 * and the `<meta>` cannot drift apart.
 *
 * **None of the three covers a top-level load** — opening the blob URL in a new
 * tab, or pasting the copied `blob:` URL. There is no frame then, so mechanism
 * 1's `sandbox` attribute and mechanism 3's `csp` attribute are both gone, and
 * the `<meta>` policy that does travel with the document has its `sandbox`
 * directive *ignored* by the HTML standard (only a real
 * `Content-Security-Policy` response header enforces it). The blob's inherited
 * origin is therefore the app's own origin in that case. Closing this needs the
 * bytes to arrive with a response header — a served preview endpoint rather
 * than a `blob:` URL — which is a backend change outside this spec; the gap is
 * recorded in the feature's evidence and raised as a decision on the task.
 *
 * ## `allow-same-origin` must NEVER be added, in any combination
 *
 * `allow-scripts` together with `allow-same-origin` lets the framed document
 * reach into the parent DOM, delete its own `sandbox` attribute and reload
 * itself unsandboxed — that is not a sandbox, it is a sandbox the page can
 * remove. It also restores the blob's inherited origin, undoing mechanism 1.
 * No amount of CSP compensates. The same goes for `allow-forms`,
 * `allow-popups`, `allow-modals`, `allow-top-navigation` (and its
 * by-user-activation variant), `allow-downloads`, `allow-pointer-lock` and
 * `allow-presentation`: the frame grants `allow-scripts` and nothing else.
 */
export function HtmlPreview({
  workspaceId,
  path,
  name,
}: {
  workspaceId: string;
  path: string;
  name: string;
}) {
  const { url, failed, failureReason, retry } = useFileObjectUrl(
    workspaceId,
    path,
    true,
    'text/html',
    injectPreviewCsp,
  );

  if (failed) {
    /*
      A sentence per reason, not one for all of them (FR-067). "Could not be
      loaded" is true of a transport failure and misleading about a file that is
      simply gone, not this reader's to read, or waiting behind a session that
      ran out — and the reader can act on the difference. The wording lives in
      `fileReadMessage` so this pane, the PDF pane and the image pane cannot
      drift (F17).
    */
    return (
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 p-4">
        <p className="text-muted-foreground text-sm">
          {fileReadMessage(failureReason, 'page')}
        </p>
        {/*
          Only for `failed` (F15). The other three reasons are answers: a file
          that is gone stays gone, a refusal stays a refusal, and an expired
          session needs a sign-in, not a second identical request.
        */}
        {failureReason === 'failed' ? <RetryButton onRetry={retry} /> : null}
      </div>
    );
  }

  if (url === undefined) {
    return (
      <div className="flex min-h-0 flex-1 p-4">
        <div
          aria-hidden="true"
          className="bg-muted min-h-64 flex-1 animate-pulse rounded-lg"
        />
      </div>
    );
  }

  return (
    <iframe
      src={url}
      title={name}
      sandbox="allow-scripts"
      referrerPolicy="no-referrer"
      csp={HTML_PREVIEW_CSP}
      className="min-h-0 w-full flex-1 border-0"
    />
  );
}
